import { useState } from 'react'
import { Link } from '@tanstack/react-router'
import { PhoneIncoming, PhoneMissed, PhoneOutgoing, Search } from 'lucide-react'
import { toast } from 'sonner'
import { Skeleton } from '@/components/ui/skeleton'
import { SearchInput } from '@/components/search-input'
import { SearchSelect } from '@/components/search-select'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { formatMessageTime } from '@/features/communications/message-format'
import { CallBody } from '@/features/phone-calls/call-details'
import { UNANSWERED, callLine } from '@/features/phone-calls/call-format'
import {
  useFileCall,
  usePhoneCalls,
  type PhoneCall,
  type PhoneCallListItem,
  type PhoneCallListParams,
} from '@/features/phone-calls/hooks'



export function CallIcon({ call }: { call: PhoneCall }) {
  const Icon = UNANSWERED.has(call.status)
    ? PhoneMissed
    : call.direction === 'incoming'
      ? PhoneIncoming
      : PhoneOutgoing
  return (
    <span className="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
      <Icon className="size-4" aria-hidden />
    </span>
  )
}

function FiledOn({ call }: { call: PhoneCallListItem }) {
  const filed = call.filed_on
  if (!filed) return null
  if (filed.kind === 'purchase_order' && filed.uuid)
    return (
      <Link to="/purchase-orders/$poId" params={{ poId: filed.uuid }} className="text-primary hover:underline">
        {filed.label}
      </Link>
    )
  if (filed.kind === 'ap_invoice')
    return (
      <Link
        to="/invoices/$invoiceId"
        params={{ invoiceId: String(filed.id) }}
        className="text-primary hover:underline"
      >
        {filed.label}
      </Link>
    )
  if (filed.kind === 'project' && filed.uuid)
    return (
      <Link to="/projects/$projectId" params={{ projectId: filed.uuid }} className="text-primary hover:underline">
        {filed.label}
      </Link>
    )
  return <span>{filed.label}</span>
}

function CallRow({ call }: { call: PhoneCallListItem }) {
  const [open, setOpen] = useState(false)
  const fileCall = useFileCall()
  const preview = call.summary?.summary[0] ?? call.voicemail_transcript ?? null
  const options = call.file_options.map((option) => ({
    value: `${option.kind}:${option.id}`,
    label: option.label,
    description: option.detail || null,
  }))
  return (
    <li className="px-3 py-2.5 sm:px-4" data-testid={`phone-call-${call.uuid}`}>
      <div className="flex items-start gap-3">
        <CallIcon call={call} />
        <div className="min-w-0 flex-1">
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            className="block w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="flex items-baseline gap-2">
              <span className="truncate text-sm font-medium">{callLine(call)}</span>
              <time
                dateTime={call.started_at}
                className="ml-auto shrink-0 text-xs tabular-nums text-muted-foreground"
              >
                {formatMessageTime(call.started_at)}
              </time>
            </span>
            {!open && preview && (
              <span className="mt-0.5 line-clamp-2 text-sm text-muted-foreground md:line-clamp-1">{preview}</span>
            )}
          </button>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            {call.filed_on ? (
              <FiledOn call={call} />
            ) : options.length > 0 ? (
              <SearchSelect
                value={undefined}
                options={options}
                placeholder="File on…"
                ariaLabel="Which record was this call about?"
                title="Which record was this call about?"
                searchPlaceholder="Search POs and invoices"
                disabled={fileCall.isPending}
                className="h-7 w-56 text-xs"
                onChange={(value) => {
                  const option = call.file_options.find((item) => `${item.kind}:${item.id}` === value)
                  if (!option) return
                  fileCall.mutate(
                    { callUuid: call.uuid, option },
                    {
                      onSuccess: () => toast.success(`Filed on ${option.label.split(' · ')[0]}`),
                      onError: () => toast.error('Could not file the call'),
                    },
                  )
                }}
              />
            ) : null}
          </div>
          {open && (
            <div className="mt-2">
              <CallBody call={call} />
            </div>
          )}
        </div>
      </div>
    </li>
  )
}

/** A supplier's or customer's Quo calls (USA-217); a call not yet on a record offers where to file it. */
export function CallsPanel({ params }: { params: PhoneCallListParams }) {
  const [search, setSearch] = useState('')
  const debounced = useDebouncedValue(search, 250)
  const calls = usePhoneCalls(params, debounced)
  const found = calls.data ?? []
  return (
    <div className="space-y-3">
      <div className="relative max-w-md">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
        <SearchInput
          type="search"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Search calls: name, number, words said"
          aria-label="Search calls"
          className="pl-8"
        />
      </div>
      {calls.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : calls.isError ? (
        <p className="text-sm text-destructive">Could not load calls.</p>
      ) : found.length === 0 ? (
        <p className="py-6 text-center text-sm text-muted-foreground">
          {debounced.trim() ? 'No calls match.' : 'No calls yet.'}
        </p>
      ) : (
        <ul className="divide-y overflow-hidden rounded-xl border bg-card">
          {found.map((call) => (
            <CallRow key={call.uuid} call={call} />
          ))}
        </ul>
      )}
    </div>
  )
}
